/**
 * LIC Proposal Stepper – ID extraction and Add vs Update decision.
 * IDs come from getlicdetailbyid; null/undefined/empty means use ADD API.
 */

export interface LicDetailIds {
  proposal_id: number | null;
  lic_id: number | null;
  identification_id: number | null;
  employment_id: number | null;
  policy_id: number | null;
  nominee_id: number | null;
  health_id: number | null;
  legal_id: number | null;
  document_id: number | null;
}

/**
 * Returns true when an existing record exists (use UPDATE API).
 * Returns false when id is null, undefined, or missing (use ADD API).
 */
export function shouldUpdate(id?: number | null): boolean {
  return id !== null && id !== undefined && Number.isFinite(id);
}

/**
 * Safely extract all IDs from getlicdetailbyid response.
 * Handles: null, undefined, empty arrays.
 * Response shape: { data?: { id, personal_detail?, otherpolicies?, nominee_details?, health_details? } }
 * Section arrays may be on the root record (d) or under personal_detail (pd); both are checked.
 */
export function extractIdsFromLicDetail(payload: unknown): LicDetailIds {
  const out: LicDetailIds = {
    proposal_id: null,
    lic_id: null,
    identification_id: null,
    employment_id: null,
    policy_id: null,
    nominee_id: null,
    health_id: null,
    legal_id: null,
    document_id: null,
  };
  if (!payload || typeof payload !== "object") return out;

  const d = (payload as any)?.data != null ? (payload as any).data : (payload as any);
  const pd = d?.personal_detail ?? null;

  // proposal_id from root record
  const rawProposalId = d?.id ?? d?.proposal_id ?? null;
  out.proposal_id = rawProposalId != null && rawProposalId !== "" ? Number(rawProposalId) : null;

  if (pd && typeof pd === "object") {
    const lid = pd.id;
    out.lic_id = lid != null && lid !== "" ? Number(lid) : null;

    const ident = pd.identity_detail;
    if (ident && typeof ident === "object" && ident.id != null) {
      out.identification_id = Number(ident.id);
    }

    const employments = Array.isArray(pd.employments) ? pd.employments : [];
    if (employments.length > 0 && employments[0]?.id != null) {
      out.employment_id = Number(employments[0].id);
    }

    const legalDetails = pd.legal_details;
    if (legalDetails && typeof legalDetails === "object" && legalDetails.id != null) {
      out.legal_id = Number(legalDetails.id);
    }

    const documents = Array.isArray(pd.documents) ? pd.documents : [];
    if (documents.length > 0 && documents[0]?.id != null) {
      out.document_id = Number(documents[0].id);
    }
  }

  // policy_id = otherpolicies[i].id — check both pd and d (API may put arrays on root)
  const otherpolicies = Array.isArray(pd?.otherpolicies) ? pd.otherpolicies : Array.isArray(d?.otherpolicies) ? d.otherpolicies : [];
  if (otherpolicies.length > 0) {
    const firstId = otherpolicies[0]?.id;
    if (firstId != null && firstId !== "") {
      const num = Number(firstId);
      if (Number.isFinite(num)) out.policy_id = num;
    }
  }

  // nominee_id = nominee_details[i].id
  const nomineeDetails = Array.isArray(pd?.nominee_details) ? pd.nominee_details : Array.isArray(d?.nominee_details) ? d.nominee_details : [];
  if (nomineeDetails.length > 0) {
    const firstId = nomineeDetails[0]?.id;
    if (firstId != null && firstId !== "") {
      const num = Number(firstId);
      if (Number.isFinite(num)) out.nominee_id = num;
    }
  }

  // health_id = health_details[0].id (single record)
  const healthDetails = Array.isArray(pd?.health_details) ? pd.health_details : Array.isArray(d?.health_details) ? d.health_details : [];
  if (healthDetails.length > 0) {
    const firstId = healthDetails[0]?.id;
    if (firstId != null && firstId !== "") {
      const num = Number(firstId);
      if (Number.isFinite(num)) out.health_id = num;
    }
  }

  return out;
}
